<template>
  <div>
    <Dialog :visible="display" position="top" :style="{ width: '528px' }">
      <template #header>
        <h1 class="title pm-txt-color bold-600">File Uploads</h1>
      </template>
      <div class="content">
        <p class="pm-txt-color title bold-600">{{ fromSnakeCase(taskTitle) }}</p>
        <p class="pm-txt-color heading bold-600">{{ externalDescription }}</p>
        <p class="pm-txt-color heading bold-600">
          Upload the required document by dragging it into the space below or click browse to select the file from your
          computer.
        </p>
        <div class="file" @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop">
          <input
            type="file"
            multiple="multiple"
            id="upload-field"
            class="w-px h-px opacity-0 overflow-hidden absolute"
            @change="onChange"
            ref="fileV"
            accept=".pdf,.jpg,.jpeg,.png"
            :key="fileKey"
          />
          <label for="upload-field" class="upload-box bold-600">
            <div class="icon-text p-d-flex p-jc-center">
              <img class="icon" :src="require('@/assets/upload_icon.svg')" alt="" />
              <p class="text">Drag and drop files here or <span>browse</span></p>
            </div>
          </label>
        </div>
        <div v-if="fileUploadList.length + documents.length > 10" class="upload-buttons">
          <div v-if="status === 'not_yet_started'">
            <Button
              :disabled="isSubmitting || isSavingForLater"
              class="close lt-txt-color"
              @click="saveForLater"
              label="Save for later"
              :loading="isSavingForLater"
            />
            <Button
              :disabled="isSubmitting || isSavingForLater || (!fileUploadList.length && !documents.length)"
              label="Submit files"
              type="primary"
              @click="submitFiles"
              :loading="isSubmitting"
            />
          </div>
          <div v-else class="upload-buttons">
            <Button :disabled="isSubmitting" class="close lt-txt-color" @click="cancelUploads" label="Cancel" />
            <Button
              :disabled="isSubmitting || (!fileUploadList.length && !documents.length)"
              label="Submit files"
              type="primary"
              @click="submitFiles"
              :loading="isSubmitting"
            />
          </div>
        </div>
        <ul class="file-list mt-4" v-if="fileUploadList.length || documents.length" v-cloak>
          <li class="text-sm p-1 p-d-flex p-ai-center" v-for="(file, i) of fileUploadList" :key="i">
            <div class="p-d-flex file-container">
              <img :src="require('@/assets/tick_icon.svg')" alt="" />
              <div class="file-details">
                <div class="progress-bar" :id="`progress-${i}`"></div>
                <p class="file-name pm-text-color">{{ file.name }}</p>
                <div v-if="file.size" class="time-size p-d-flex">
                  <p class="size lt-txt-color">
                    <span class="error-class" v-if="file.size > 1_000_000_000">Exceeds maximum file size 1 GB</span>
                    <span v-else>Size: {{ formatFileSize(file.size) }}</span>
                  </p>
                </div>
              </div>
            </div>
            <img class="icon" @click="removePendingUpload(file)" :src="require('@/assets/cross_icon.svg')" alt="" />
          </li>
          <li class="text-sm p-1 p-d-flex p-ai-center" v-for="document of documents" :key="document.id">
            <div class="p-d-flex file-container">
              <img :src="require('@/assets/tick_icon.svg')" alt="" />
              <div class="file-details">
                <p class="file-name pm-text-color">{{ formatFileName(document.s3FilePath) }}</p>
              </div>
            </div>
            <img
              v-if="status === 'not_yet_started'"
              class="icon"
              @click="removeUploadedFile(document.s3FilePath)"
              :src="require('@/assets/cross_icon.svg')"
              alt=""
            />
          </li>
        </ul>
      </div>
      <div class="upload-buttons">
        <div v-if="status === 'not_yet_started'">
          <Button
            :disabled="isSubmitting || isSavingForLater"
            class="close lt-txt-color"
            @click="saveForLater"
            label="Save for later"
            :loading="isSavingForLater"
          />
          <Button
            :disabled="isSubmitting || isSavingForLater || (!fileUploadList.length && !documents.length)"
            label="Submit files"
            type="primary"
            @click="submitFiles"
            :loading="isSubmitting"
          />
        </div>
        <div v-else>
          <Button :disabled="isSubmitting" class="close lt-txt-color" @click="cancelUploads" label="Cancel" />
          <Button
            :disabled="isSubmitting || (!fileUploadList.length && !documents.length)"
            label="Submit files"
            type="primary"
            @click="submitFiles"
            :loading="isSubmitting"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { deleteSupportingDocument, uploadFileToS3, uploadSupportingDocuments } from '@/services/FileUploadService';
import { fromSnakeCase } from '@/services/formatHelper';
import Dialog from 'primevue/dialog';
import Screensize from '@/mixins/screensize.mixin';

const ALLOWED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const MAX_SIZE = 1_000_000_000;

export default {
  mixins: [Screensize],
  components: { Dialog },
  emits: ['skipped', 'completed'],
  props: ['display', 'openDialog', 'closeDialog', 'status', 'taskTitle', 'externalDescription', 'taskId', 'documents'],
  data() {
    return {
      fileUploadList: [],
      isSubmitting: false,
      isSavingForLater: false,
      fileKey: 0,
    };
  },
  setup() {
    return {
      fromSnakeCase,
    };
  },
  methods: {
    isAllowedFile(file) {
      const allowedTypes = new Set(['application/pdf', 'image/jpeg', 'image/png']);
      const ext = (file.name?.split('.').pop() || '').toLowerCase();
      const allowedExt = new Set(['pdf', 'jpeg', 'jpg', 'png']);
      return allowedTypes.has(file.type) || allowedExt.has(ext);
    },
    /**
     * Event handler for when the file input changes by way
     * of file(s) being selected
     */
    onChange() {
      this.processSelectedFiles(Array.from(this.$refs.fileV.files));
    },

    /**
     * Extracts file name from S3 bucket path
     */
    formatFileName(fileName) {
      const fileOnly = fileName.split('/').pop();
      return fileOnly;
    },

    /**
     * Formats file size in KB or MB
     */
    formatFileSize(totalBytes) {
      if (totalBytes < 1_000_000) {
        return (totalBytes / 1000).toFixed(1) + 'KB';
      } else {
        return (totalBytes / 1_000_000).toFixed(1) + 'MB';
      }
    },

    /**
     * Submits the files and leaves the task incomplete
     */
    async saveForLater() {
      await this.uploadFiles(false);
    },

    /**
     * Submits the files and completes the task
     */
    async submitFiles() {
      await this.uploadFiles(true);
    },

    /**
     * Cancels and closes the modal without uploading
     */
    async cancelUploads() {
      return this.$emit('skipped');
    },

    /**
     * Removes a newly-added file from the upload queue
     */
    removePendingUpload(file) {
      const index = this.fileUploadList.indexOf(file);
      if (index > -1) {
        this.fileUploadList.splice(index, 1);
        this.fileKey += 1;
      }
    },

    /**
     * Event handler for entering the container when dragging files
     */
    onDragOver(event) {
      event.preventDefault();
      if (!event.currentTarget.classList.contains('bg-green-300')) {
        event.currentTarget.classList.add('bg-green-300');
      }
    },

    /**
     * Event handler for leaving the container when dragging files
     */
    onDragLeave(event) {
      event.currentTarget.classList.remove('bg-green-300');
    },

    /**
     * Event handler for dropping files
     */
    onDrop(event) {
      event.preventDefault();
      this.processSelectedFiles(Array.from(event.dataTransfer.files));
      event.currentTarget.classList.remove('bg-green-300');
    },
    processSelectedFiles(files) {
      let rejected = 0;
      for (const f of files) {
        if (!ALLOWED_TYPES.has(f.type)) {
          rejected++;
          continue;
        }
        // keep showing the size warning in the list but still add valid-type files;
        // or skip >1GB entirely if desired:
        this.fileUploadList.push(f);
      }
      if (rejected) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unsupported file type. Please upload a PDF, JPEG, or PNG file.',
          life: 3000,
        });
      }
      this.fileKey += 1; // reset input so the same file can be re-selected
    },

    /**
     * Submits the pending uploads to the backend server to create the
     * signed URLs required for uploading to S3, and optionally "completes"
     * the task by assigning it back to CRM users for review
     */
    async uploadFiles(completeTask) {
      const uploads = [];
      const uploadIndexes = [];

      for (let i = 0; i < this.fileUploadList.length; i += 1) {
        const f = this.fileUploadList[i];
        if (f.size < MAX_SIZE) {
          uploads.push(f);
          uploadIndexes.push(i);
        }
      }

      // Defensive check — primary validation happens at selection time.
      if (uploads.some((f) => !ALLOWED_TYPES.has(f.type))) {
        return this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unsupported file type. Please upload a PDF, JPEG, or PNG file.',
          life: 3000,
        });
      }

      if (!uploads.length && !completeTask) {
        return this.$emit('skipped');
      }

      this.setSubmittingAndSavingForLater(completeTask, !completeTask);

      if (!uploads.length) {
        await uploadSupportingDocuments(this.taskId, [], completeTask);
        this.setSubmittingAndSavingForLater(false, false);
        return this.$emit('completed');
      }

      let signedUrls;

      try {
        signedUrls = await uploadSupportingDocuments(this.taskId, uploads, false);
      } catch (error) {
        // this allows the isSubmitting and isSavingForLater to be false when there is an error
        this.setSubmittingAndSavingForLater(false, false);

        if (error.response && error.response.data && error.response.data.message) {
          const errorMessage = error.response.data.message.includes('content-type-error')
            ? 'Unsupported file type. Please upload a PDF, JPEG, or PNG file.'
            : error.response.data.message;

          return this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 3000,
          });
        } else {
          return this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong',
            life: 3000,
          });
        }
      }

      if (!signedUrls.length) {
        if (completeTask) {
          await uploadSupportingDocuments(this.taskId, [], true);
        }

        return this.$emit('completed');
      }

      try {
        Promise.all(
          signedUrls.map((signedUrl) => {
            const index = signedUrls.indexOf(signedUrl);
            const file = uploads[index];
            const progressBar = document.getElementById(`progress-${index}`);
            return uploadFileToS3(signedUrl, file, progressBar);
          }),
        ).then(async () => {
          this.setSubmittingAndSavingForLater(false, false);

          if (completeTask) {
            await uploadSupportingDocuments(this.taskId, [], true);
          }

          this.$emit('completed');
        });
      } catch (error) {
        await this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong',
          life: 3000,
        });
      } finally {
        this.setSubmittingAndSavingForLater(false, false);
      }
    },
    setSubmittingAndSavingForLater(isSubmitting, isSavingForLater) {
      this.isSubmitting = isSubmitting;
      this.isSavingForLater = isSavingForLater;
    },

    /**
     * Removes an already-uploaded file from the supporting document
     */
    async removeUploadedFile(fileName) {
      try {
        const document = this.documents.find((document) => document.s3FilePath === fileName);
        await deleteSupportingDocument(document.id);
        this.documents.splice(this.documents.indexOf(document), 1);
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          await this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error deleting upload',
            life: 3000,
          });
        } else {
          await this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong',
            life: 3000,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

[v-cloak] {
  display: none;
}

.file-list {
  width: 100%;

  h2 {
    font-size: 1rem;
  }

  li {
    width: 100%;
    gap: 1rem;
    justify-content: space-between;
    border-bottom: 1px solid $light-text-color;

    .progress-bar {
      height: 0.25rem;
      line-height: 0.25rem;
      display: block;
      width: 0;
      background-color: $primary-color;
    }

    .file-details {
      flex-grow: 1;
    }

    .file-container {
      flex-grow: 1;
      gap: 1rem;
      padding: 1rem 0;
      align-items: center;
    }

    .file-name {
      margin: 0;
      font-size: 0.875rem;
    }

    .icon {
      cursor: pointer;
    }
  }

  li:last-of-type {
    border-bottom: none;

    div {
      padding-bottom: 0;
    }
  }
}

.title {
  font-size: 1.25rem;
}

.content {
  .heading {
    font-size: 0.875rem;
  }
}

.file {
  margin: 1.5rem 0 1rem 0;

  .icon-text {
    gap: 1rem;

    .text {
      margin: 0;
      font-size: 0.875rem;

      span {
        color: $primary-color;
      }
    }
  }
}

.upload-buttons {
  gap: 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: 992px) {
    // gap: 0;
    justify-content: space-between;
  }

  .close {
    background-color: #fff;
    border-color: #fff;
    cursor: pointer;
  }
  .close:hover {
    background-color: #fff !important;
    border-color: #fff !important;
    color: #061238 !important;
  }
}

.upload-box {
  width: 100%;
  border: 2px dashed #54b7f9;
  background: #54b7f91a;
  border-radius: 5px;
  display: block;
  padding: 1rem 1rem;
  transition: border 300ms ease;
  cursor: pointer;
  text-align: center;
}
.bg-green-300 {
  background-color: #ffffff !important;
}
</style>
